import React, { useCallback, useState } from 'react';

import s from '../Authorization.module.css';
import { BackStepItem } from '../BackStep/BackStepItem';
import { ButtonContainer } from '../Button/Button';
import AuthorizationCheckbox from '../Checkbox/Checkbox';
import { Header } from '../Header/Header';
import { Input } from '../Input/Input';
import { PasswordInput } from '../PasswordInput/PasswordInput';
import { FormTitle } from '../Register/FormTitle/FormTitle';
import { Form } from '../Register/Step/Form';
import { validateEmail, validateFormatEmail } from '../validate';

export const Login = () => {
    const subtitle = () => (
        <span>
            {i18next.t('Login.return.subtitle.part1')}
            <a href={`${BASE_URL}/register`}>{i18next.t('Login.return.subtitle.part2')}</a>
        </span>
    );
    const LoginLabel = () => (
        <span className={s.acceptText}>{i18next.t('Login.return.rememberMe')}</span>
    );

    const [errors, setErrors] = useState({
        email: null,
        password: null,
    });

    const [serverError, setServerError] = useState('');

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(true);

    const validatePassword = (value) => {
        if (value.length === 0) {
            if (serverError) {
                setServerError('');
            }
            return i18next.t('Login.return.validatePassword.enterPassword');
        }

        return null;
    };

    const isEmptyFields = !email.length && !password.length;
    const isButtonDisabled = isEmptyFields;

    const sendLoginRequest = useCallback(() => {
        const bodyJson = {
            _username: email,
            _password: password,
            // eslint-disable-next-line camelcase
            _csrf_token: window.csrf_token,
        };
        if (rememberMe) {
            // eslint-disable-next-line camelcase,no-underscore-dangle
            bodyJson._remember_me = 'on';
        }

        fetch('/login_check', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json;charset=utf-8',
            },
            body: JSON.stringify(bodyJson),
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.error) {
                    setServerError(data.error);
                }

                if (data.success) {
                    window.location.href = data.url;
                }
            })
            .catch((err) => {
                window.console.error(`[Reset] ${err}`);
            });
    }, [email, password, rememberMe]);

    const buttonClick = () => {
        if (errors.email === '' || password === '') {
            setErrors((prevState) => ({
                ...prevState,
                email: validateEmail(email) || validateFormatEmail(email),
                password: validatePassword(password),
            }));
        } else if (errors.email === null && errors.password === null) {
            sendLoginRequest();
        }
    };

    return (
        <div className={s.authorizationBlock}>
            <Header type="mobile" needShowBack />
            <div className={s.content}>
                <div className={s.mainImage} />
                <div className={s.formWrapper}>
                    <div className={s.loginForm}>
                        <Header type="desktop" needShowBack />
                        <div className={s.loginBlock}>
                            <FormTitle
                                title={i18next.t('Login.return.title')}
                                subtitle={subtitle()}
                            />
                            <Form>
                                <Input
                                    label={i18next.t('Login.return.form.input.label')}
                                    type="email"
                                    error={!!errors.email}
                                    helperText={errors.email ?? ' '}
                                    value={email}
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        setEmail(value);
                                        const error = validateEmail(value);
                                        setErrors((prevState) => ({
                                            ...prevState,
                                            email: error,
                                        }));
                                    }}
                                    onBlur={(e) => {
                                        const error = validateEmail(e.target.value);
                                        setErrors((prevState) => ({
                                            ...prevState,
                                            email: error,
                                        }));
                                        if (error === null) {
                                            setErrors((prevState) => ({
                                                ...prevState,
                                                email: validateFormatEmail(e.target.value),
                                            }));
                                        }
                                    }}
                                    inputProps={{
                                        'data-id': 'authorization-email',
                                    }}
                                />
                                <PasswordInput
                                    updatePassword={setPassword}
                                    setErrors={setErrors}
                                    errors={errors}
                                    label={i18next.t('Login.return.passwordInput.label')}
                                    id="login-password"
                                    validatePassword={validatePassword}
                                    tooltip={false}
                                />
                                <div className={s.bottomBlock}>
                                    <AuthorizationCheckbox
                                        value
                                        saveCheckbox={setRememberMe}
                                        label={<LoginLabel />}
                                        required={false}
                                        position="center"
                                    />
                                    <a href={`${BASE_URL}/forgot-password`}>
                                        {i18next.t('Login.return.bottomBlock.a.forgotPassword')}
                                    </a>
                                </div>
                                <div className={s.errorWrapper}>
                                    {serverError && <div className={s.error}>{serverError}</div>}
                                </div>
                                <ButtonContainer
                                    textButton={i18next.t(
                                        'Login.return.ButtonContainer.textButton'
                                    )}
                                    disable={isButtonDisabled}
                                    needShowLoader={false}
                                    onClick={buttonClick}
                                />
                            </Form>
                            <div className={s.stepBackMobile}>
                                <a href={ENV.BASE_URL}>
                                    <BackStepItem
                                        text={i18next.t('Login.return.stepBackMobile.BackStepItem')}
                                    />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
